import i18n from "@/i18n";
import moment, { Moment } from "moment";

const { t: $t, d: $d } = i18n.global;

type BackendKey = {
  singular: string;
  plural?: string;
};

export interface RelativeTimeOption {
  key: string;
  backendKey: BackendKey | undefined;
  type: "singleVar" | "singleFix" | "range";
  groupId: number;
  sequence: number;
  display: string;
}

export const relativeTimeOptions: RelativeTimeOption[] = [
  {
    key: "today",
    backendKey: { singular: "today" },
    type: "singleFix",
    groupId: 1,
    sequence: 1,
    display: $t("relativeDateTimePicker.today")
  },
  {
    key: "yesterday",
    backendKey: { singular: "yesterday" },
    type: "singleFix",
    groupId: 1,
    sequence: 2,
    display: $t("relativeDateTimePicker.yesterday")
  },
  {
    key: "tomorrow",
    backendKey: { singular: "tomorrow" },
    type: "singleFix",
    groupId: 1,
    sequence: 3,
    display: $t("relativeDateTimePicker.tomorrow")
  },
  {
    key: "thisWeek",
    backendKey: { singular: "this week" },
    type: "range",
    groupId: 2,
    sequence: 1,
    display: $t("relativeDateTimePicker.thisWeek")
  },
  {
    key: "lastWeek",
    backendKey: { singular: "last week" },
    type: "range",
    groupId: 2,
    sequence: 2,
    display: $t("relativeDateTimePicker.lastWeek")
  },
  {
    key: "nextWeek",
    backendKey: { singular: "next week" },
    type: "range",
    groupId: 2,
    sequence: 3,
    display: $t("relativeDateTimePicker.nextWeek")
  },
  {
    key: "thisMonth",
    backendKey: { singular: "this month" },
    type: "range",
    groupId: 2,
    sequence: 4,
    display: $t("relativeDateTimePicker.thisMonth")
  },
  {
    key: "lastMonth",
    backendKey: { singular: "last month" },
    type: "range",
    groupId: 2,
    sequence: 5,
    display: $t("relativeDateTimePicker.lastMonth")
  },
  {
    key: "nextMonth",
    backendKey: { singular: "next month" },
    type: "range",
    groupId: 2,
    sequence: 6,
    display: $t("relativeDateTimePicker.nextMonth")
  },
  {
    key: "hoursAgo",
    backendKey: { singular: "1 hour ago", plural: "{value} hours ago" },
    type: "singleVar",
    groupId: 3,
    sequence: 1,
    display: $t("relativeDateTimePicker.hoursAgo")
  },
  {
    key: "daysAgo",
    backendKey: { singular: "1 day ago", plural: "{value} days ago" },
    type: "singleVar",
    groupId: 3,
    sequence: 2,
    display: $t("relativeDateTimePicker.daysAgo")
  },
  {
    key: "weeksAgo",
    backendKey: { singular: "1 week ago", plural: "{value} weeks ago" },
    type: "singleVar",
    groupId: 3,
    sequence: 3,
    display: $t("relativeDateTimePicker.weeksAgo")
  },
  {
    key: "monthsAgo",
    backendKey: { singular: "1 month ago", plural: "{value} months ago" },
    type: "singleVar",
    groupId: 3,
    sequence: 4,
    display: $t("relativeDateTimePicker.monthsAgo")
  },
  {
    key: "inHours",
    backendKey: { singular: "in 1 hour", plural: "in {value} hours" },
    type: "singleVar",
    groupId: 3,
    sequence: 5,
    display: $t("relativeDateTimePicker.inHours")
  },
  {
    key: "inDays",
    backendKey: { singular: "in 1 day", plural: "in {value} days" },
    type: "singleVar",
    groupId: 3,
    sequence: 6,
    display: $t("relativeDateTimePicker.inDays")
  },
  {
    key: "inWeeks",
    backendKey: { singular: "in 1 week", plural: "in {value} weeks" },
    type: "singleVar",
    groupId: 3,
    sequence: 7,
    display: $t("relativeDateTimePicker.inWeeks")
  },
  {
    key: "inMonths",
    backendKey: { singular: "in 1 month", plural: "in {value} months" },
    type: "singleVar",
    groupId: 3,
    sequence: 8,
    display: $t("relativeDateTimePicker.inMonths")
  }
];

interface TimeOptionInput {
  key: string | undefined;
  value?: number | string;
  baseDate?: Moment;
}

interface TimeResult {
  type: "single" | "range" | "empty";
  date?: Moment;
  from?: Moment;
  to?: Moment;
  display: string;
}

export function getRelativeDate({ key, value = 1, baseDate = moment() }: TimeOptionInput): TimeResult {
  const date = baseDate.clone();
  const num = parseInt(value.toString());
  let result: TimeResult;

  switch (key) {
    case "today":
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePicker.today") + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    case "yesterday":
      date.subtract(1, "day");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePicker.yesterday") + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    case "tomorrow":
      date.add(1, "day");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePicker.tomorrow") + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    case "thisWeek": {
      const from = date.clone().startOf("isoWeek");
      const to = date.clone().endOf("isoWeek");
      result = {
        type: "range",
        from,
        to,
        display: $t("relativeDateTimePicker.thisWeek") + ` (${$d(from.toDate(), "shortDate")} - ${$d(to.toDate(), "shortDate")})`
      };
      break;
    }
    case "lastWeek": {
      const from = date.clone().subtract(1, "week").startOf("isoWeek");
      const to = from.clone().endOf("isoWeek");
      result = {
        type: "range",
        from,
        to,
        display: $t("relativeDateTimePicker.lastWeek") + ` (${$d(from.toDate(), "shortDate")} - ${$d(to.toDate(), "shortDate")})`
      };
      break;
    }
    case "nextWeek": {
      const from = date.clone().add(1, "week").startOf("isoWeek");
      const to = from.clone().endOf("isoWeek");
      result = {
        type: "range",
        from,
        to,
        display: $t("relativeDateTimePicker.nextWeek") + ` (${$d(from.toDate(), "shortDate")} - ${$d(to.toDate(), "shortDate")})`
      };
      break;
    }
    case "thisMonth": {
      const from = date.clone().startOf("month");
      const to = date.clone().endOf("month");
      result = {
        type: "range",
        from,
        to,
        display: $t("relativeDateTimePicker.thisMonth") + ` (${$d(from.toDate(), "shortDate")} - ${$d(to.toDate(), "shortDate")})`
      };
      break;
    }
    case "lastMonth": {
      const from = date.clone().subtract(1, "month").startOf("month");
      const to = from.clone().endOf("month");
      result = {
        type: "range",
        from,
        to,
        display: $t("relativeDateTimePicker.lastMonth") + ` (${$d(from.toDate(), "shortDate")} - ${$d(to.toDate(), "shortDate")})`
      };
      break;
    }
    case "nextMonth": {
      const from = date.clone().add(1, "month").startOf("month");
      const to = from.clone().endOf("month");
      result = {
        type: "range",
        from,
        to,
        display: $t("relativeDateTimePicker.nextMonth") + ` (${$d(from.toDate(), "shortDate")} - ${$d(to.toDate(), "shortDate")})`
      };
      break;
    }
    case "hoursAgo": {
      date.subtract(num, "hours");
      console.log(num);
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.hoursAgo", { count: num }) + ` (${$d(date.toDate(), "shortDateTime")})`
      };
      break;
    }
    case "daysAgo": {
      date.subtract(num, "days");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.daysAgo", { count: num }) + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    }
    case "weeksAgo": {
      date.subtract(num, "weeks");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.weeksAgo", { count: num }) + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    }
    case "monthsAgo": {
      date.subtract(num, "months");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.monthsAgo", { count: num }) + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    }
    case "inHours": {
      date.add(num, "hours");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.inHours", { count: num }) + ` (${$d(date.toDate(), "shortDateTime")})`
      };
      break;
    }
    case "inDays": {
      date.add(num, "days");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.inDays", { count: num }) + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    }
    case "inWeeks": {
      date.add(num, "weeks");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.inWeeks", { count: num }) + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    }
    case "inMonths": {
      date.add(num, "months");
      result = {
        type: "single",
        date,
        display: $t("relativeDateTimePreview.inMonths", { count: num }) + ` (${$d(date.toDate(), "shortDate")})`
      };
      break;
    }
    default:
      result = {
        type: "empty",
        display: $t("NoTimeSelected")
      };
      break;
  }

  return result;
}
